import { useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import moment from 'moment'
import { Alert, DatePicker, InputNumber, Modal, Tag } from 'antd'
import { faArrowRotateLeft, faBan, faCheck, faPen } from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { ColorsPalette } from '../../../../config/colors'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import TooltipIconButton from '../../../../Components/antd/Buttons/TooltipIconButton'
import ConfirmFormFooter from '../../../../Components/antd/Forms/ConfirmFormFooter'
import SectorSelector from '../../../../Components/shared/Selectors/SectorSelector'
import { getIdOrNull, isNonEmptyArray } from '../../../../utils'
import { EUROPEAN_DATE } from '../../../../utils/constants'
import { DAY, getTimezoneLessMoment } from '../../../../utils/momentjs'
import { RequestPeriod } from '../../../../utils/entities/requestPeriod'
import {
  countGrantedPlaces, countTwinRequests, getCoordinatorFullname, groupRequests, isClosed, isPeriodStillOpen,
  joinsExistingSchool, summarizeGrantedPeriods
} from '../../../../utils/entities/schoolPortalRequest'

const { RangePicker } = DatePicker

const EXCLUDED_STYLE = { textDecoration: 'line-through', opacity: 0.5 }
const HINT_STYLE = { fontSize: '0.75em', color: ColorsPalette.GREY }

const EMPTY = '—'
const NO_PLACE = 0

const BODY_STYLE = { maxHeight: '70vh', overflowY: 'auto' }

const getSectionLabel = line => [line?.sectionToYear?.sectionAbbreviation, line?.sectionToYear?.schoolYearName]
  .filter(part => (part ?? '') !== '')
  .join(' — ')

const formatDates = dates => {
  const startDate = getTimezoneLessMoment(dates?.startDate)
  const endDate = getTimezoneLessMoment(dates?.endDate)

  return (startDate === null || endDate === null)
    ? '—'
    : `${startDate.format(EUROPEAN_DATE)} → ${endDate.format(EUROPEAN_DATE)}`
}

const placesOn = (state, index) => state?.placesByPeriod?.[index] ?? NO_PLACE

const totalPlacesOf = state => Object.values(state?.placesByPeriod ?? {})
  .reduce((total, places) => total + places, NO_PLACE)

const clampPlaces = (value, max) => Math.min(Math.max(value ?? NO_PLACE, NO_PLACE), max)

const firstOpenPeriodIndex = group => (group.sample.requestPeriods ?? []).findIndex(isPeriodStillOpen)

const isGroupExpired = group => firstOpenPeriodIndex(group) === -1

const buildGroupState = groups => groups.reduce((state, group) => {
  const openIndex = firstOpenPeriodIndex(group)

  return {
    ...state,
    [group.key]: {
      removed: false,
      placesByPeriod: openIndex === -1 ? {} : { [openIndex]: group.requests.length },
      sector: getIdOrNull(group.sample.sector),
      periodDates: {}
    }
  }
}, {})

const AcceptSchoolRequestModal = ({ loading = false, readOnly = false, schoolPortalRequest, onCancel, onConfirm }) => {
  const t = useSelector(selectTranslate)

  const { sectorsOrderByName } = useContext(InstitutionContext) ?? {}

  const groups = useMemo(() => groupRequests(schoolPortalRequest?.requests), [schoolPortalRequest])

  const [groupState, setGroupState] = useState({})
  const [adjusting, setAdjusting] = useState({})

  useEffect(() => {
    setGroupState(buildGroupState(groups))
    setAdjusting(groups.reduce(
      (state, group) => getIdOrNull(group.sample.sector) === null && !isGroupExpired(group)
        ? { ...state, [group.key]: true }
        : state,
      {}
    ))
  }, [groups])

  const patchGroup = (key, patch) => setGroupState(state => ({
    ...state,
    [key]: { ...state[key], ...patch }
  }))

  const keptPeriods = group => {
    const state = groupState[group.key]

    return (group.sample.requestPeriods ?? [])
      .map((period, index) => ({ period, index, places: placesOn(state, index) }))
      .filter(({ places }) => places > NO_PLACE)
  }

  const keptPlaces = useMemo(() => groups.reduce(
    (total, group) => total + (groupState[group.key]?.removed ? 0 : totalPlacesOf(groupState[group.key])), 0
  ), [groups, groupState])

  const requestedPlaces = useMemo(
    () => groups.reduce((total, group) => total + group.requests.length, 0),
    [groups]
  )

  const canAccept = keptPlaces > 0 && groups.every(group => {
    const state = groupState[group.key]

    return state?.removed ||
      isGroupExpired(group) ||
      (getIdOrNull(state?.sector) !== null && totalPlacesOf(state) > NO_PLACE)
  })

  const buildRequests = () => groups.flatMap(group => {
    const state = groupState[group.key]

    if (state.removed) {
      return []
    }

    return keptPeriods(group)
      .flatMap(({ index, places }) => Array(places).fill(index))
      .map((periodIndex, place) => {
        const request = group.requests[place]
        const period = (request.requestPeriods ?? [])[periodIndex]
        const dates = state.periodDates[periodIndex] ?? period

        return {
          id: request.id,
          sector: state.sector,
          requestPeriods: [new RequestPeriod({
            id: period.id,
            startDate: moment(dates.startDate),
            endDate: moment(dates.endDate)
          }).toRequestBody()]
        }
      })
  })

  const buildDisabledDate = period => {
    const requestedStart = getTimezoneLessMoment(period.startDate)
    const requestedEnd = getTimezoneLessMoment(period.endDate)

    return date => !date.isAfter(moment(), DAY) ||
      date.isBefore(requestedStart, DAY) ||
      date.isAfter(requestedEnd, DAY)
  }

  const renderPeriods = group => {
    const state = groupState[group.key] ?? {}
    const assigned = totalPlacesOf(state)

    return (
      <div className='flex-column'>
        {(group.sample.requestPeriods ?? []).map((period, index) => {
          const dates = state.periodDates?.[index] ?? period
          const places = placesOn(state, index)
          const isOpen = isPeriodStillOpen(period)
          const available = isOpen ? group.requests.length - assigned + places : NO_PLACE

          return (
            <div
              key={`period-${group.key}-${index}`}
              className={places === NO_PLACE ? 'flex-row mb-4 ai-ctr period-row period-removed' : 'flex-row mb-4 ai-ctr period-row'}
            >
              <RangePicker
                allowClear={false}
                format={EUROPEAN_DATE}
                disabled={!isOpen || places === NO_PLACE}
                value={[getTimezoneLessMoment(dates.startDate), getTimezoneLessMoment(dates.endDate)]}
                disabledDate={buildDisabledDate(period)}
                onChange={selectedDates => patchGroup(group.key, {
                  periodDates: {
                    ...state.periodDates,
                    [index]: { startDate: selectedDates[0], endDate: selectedDates[1] }
                  }
                })}
              />
              {isOpen ? (
                <>
                  <InputNumber
                    className='ml-4'
                    min={NO_PLACE}
                    max={available}
                    value={places}
                    onChange={value => patchGroup(group.key, {
                      placesByPeriod: { ...state.placesByPeriod, [index]: clampPlaces(value, available) }
                    })}
                  />
                  <span className='ml-4' style={HINT_STYLE}>{t('school_requests_table.accept.places_on_period')}</span>
                </>
              ) : (
                <Tag className='ml-4'>{t('school_requests_table.accept.period_expired')}</Tag>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderPeriodRow = (group, period, index, showPlaces) => (
    <div key={`period-${group.key}-${index}`}>
      {formatDates(period)}
      {showPlaces && (
        <span style={HINT_STYLE}> — {t('school_portal.tracking.places', { places: period.places })}</span>
      )}
    </div>
  )

  const summarize = group => {
    const state = groupState[group.key] ?? {}
    const sector = (sectorsOrderByName ?? []).find(s => getIdOrNull(s) === getIdOrNull(state.sector))
    const kept = keptPeriods(group).map(({ period, index, places }) => ({
      id: period.id ?? index,
      places,
      ...(state.periodDates?.[index] ?? period)
    }))
    const periods = kept.map(formatDates).join(' · ')

    return {
      periodsData: kept,
      places: totalPlacesOf(state),
      section: getSectionLabel(group.sample),
      sector: sector?.name ?? group.sample.sector?.name ?? EMPTY,
      periods: periods === '' ? EMPTY : periods
    }
  }

  const isAnswered = isClosed(schoolPortalRequest)

  const grantedPlaces = useMemo(
    () => countGrantedPlaces(schoolPortalRequest?.requests),
    [schoolPortalRequest]
  )

  const answeredNeeds = useMemo(
    () => !readOnly || !isAnswered
      ? []
      : groups
        .map(group => ({ ...group, requests: group.requests.filter(line => isNonEmptyArray(line.grantedPeriods)) }))
        .filter(group => isNonEmptyArray(group.requests)),
    [groups, readOnly, isAnswered]
  )

  const renderAnsweredRow = group => {
    const sample = group.requests[0] ?? group.sample
    const periods = isAnswered ? summarizeGrantedPeriods(group.requests) : (sample.requestPeriods ?? [])
    const places = isAnswered ? countGrantedPlaces(group.requests) : group.requests.length
    const hasComment = (sample.comment ?? '') !== ''

    return [
      <tr key={group.key} className={hasComment ? 'no-border' : ''}>
        <td data-label={t('form.label.section')}>
          <b>{getSectionLabel(sample)}</b>
        </td>

        <td data-label={t('form.label.sector')}>{sample.sector?.name ?? EMPTY}</td>

        <td className='periods' data-label={t('form.label.periods')}>
          {isNonEmptyArray(periods)
            ? periods.map((period, index) => renderPeriodRow(group, period, index, isAnswered && periods.length > 1))
            : EMPTY}
        </td>

        <td className='numeric' data-label={t('school_portal.requests.places')}>
          <b>{places}</b>
        </td>
      </tr>,

      hasComment && (
        <tr key={`comment-${group.key}`} className='extras'>
          <td colSpan={4}><i>{sample.comment}</i></td>
        </tr>
      )
    ]
  }

  const asRequested = group => {
    const periods = (group.sample.requestPeriods ?? []).map(formatDates).join(' · ')

    return {
      places: group.requests.length,
      sector: group.sample.sector?.name ?? EMPTY,
      periods: periods === '' ? EMPTY : periods
    }
  }

  const expiryNotice = useMemo(() => {
    if (readOnly || !isNonEmptyArray(groups)) {
      return null
    }

    if (groups.every(isGroupExpired)) {
      return t('school_requests_table.accept.all_expired')
    }

    return groups.some(isGroupExpired) ? t('school_requests_table.accept.some_expired') : null
  }, [readOnly, groups, t])

  const mergeNotice = useMemo(() => {
    if (readOnly || schoolPortalRequest === null) {
      return null
    }

    if (joinsExistingSchool(schoolPortalRequest)) {
      return t('school_requests_table.accept.joins_existing_school', { school: schoolPortalRequest?.school?.name })
    }

    const twins = countTwinRequests(schoolPortalRequest)

    return twins > 0 ? t('school_requests_table.accept.twin_pending', { count: twins }) : null
  }, [readOnly, schoolPortalRequest, t])

  return (
    <Modal
      className='school-request-answer'
      bodyStyle={BODY_STYLE}
      title={t(readOnly ? 'school_requests_table.consult.title' : 'school_requests_table.accept.title')}
      visible={schoolPortalRequest !== null}
      centered
      width='95vw'
      destroyOnClose
      footer={readOnly ? null : (
        <ConfirmFormFooter
          confirmTitle='school_requests_table.accept.confirm'
          cancelTitle='common.cancel'
          disabled={loading || !canAccept}
          onCancel={onCancel}
          onConfirm={() => onConfirm(buildRequests())}
        />
      )}
      onCancel={onCancel}
    >
      {expiryNotice !== null && (
        <Alert
          className='mb-16'
          type='warning'
          message={expiryNotice}
          showIcon
        />
      )}

      {mergeNotice !== null && (
        <Alert
          className='mb-16'
          type='info'
          message={mergeNotice}
          showIcon
        />
      )}

      <div className='modal-inner-container mb-12'>
        <div><b>{schoolPortalRequest?.school?.name}</b></div>
        <div>{getCoordinatorFullname(schoolPortalRequest?.coordinator)} — {schoolPortalRequest?.coordinator?.email}</div>
      </div>

      <div className='table-wrapper'>
        <table className='requests'>
          <thead>
            <tr>
              <th>{t('form.label.section')}</th>
              <th>{t('form.label.sector')}</th>
              <th>{t('form.label.periods')}</th>
              <th className='numeric'>{t('school_portal.requests.places')}</th>
              {!readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {readOnly && (isAnswered ? answeredNeeds : groups).map(renderAnsweredRow)}
            {readOnly && isAnswered && !isNonEmptyArray(answeredNeeds) && (
              <tr>
                <td colSpan={4}><i>{t('school_requests_table.consult.nothing_granted')}</i></td>
              </tr>
            )}
            {!readOnly && groups.map(group => {
              const state = groupState[group.key] ?? {}
              const expired = isGroupExpired(group)
              const excluded = state.removed || expired
              const isAdjusting = adjusting[group.key] === true && !excluded
              const granted = summarize(group)
              const requested = asRequested(group)

              return [
                <tr key={group.key} className={(group.sample.comment ?? '') !== '' ? 'no-border' : ''}>
                  <td data-label={t('form.label.section')}>
                    <b style={excluded ? EXCLUDED_STYLE : undefined}>{granted.section}</b>
                    {excluded && (
                      <div>
                        <Tag color={expired ? 'orange' : undefined}>
                          {t(expired
                            ? 'school_requests_table.accept.line_expired'
                            : 'school_requests_table.accept.line_refused')}
                        </Tag>
                      </div>
                    )}
                  </td>

                  <td data-label={t('form.label.sector')}>
                    {isAdjusting ? (
                      <SectorSelector
                        sectors={sectorsOrderByName ?? []}
                        values={getIdOrNull(state.sector)}
                        onChange={value => patchGroup(group.key, { sector: value })}
                      />
                    ) : (state.removed ? EMPTY : granted.sector)}
                    {!state.removed && granted.sector !== requested.sector && (
                      <div style={HINT_STYLE}>
                        {t('school_requests_table.accept.requested_value', { value: requested.sector })}
                      </div>
                    )}
                  </td>

                  <td className='periods' data-label={t('form.label.periods')}>
                    {isAdjusting && renderPeriods(group)}
                    {!isAdjusting && (state.removed || !isNonEmptyArray(granted.periodsData)
                      ? EMPTY
                      : granted.periodsData.map((period, index) => renderPeriodRow(
                        group, period, index, granted.periodsData.length > 1
                      )))}
                    {!state.removed && granted.periods !== requested.periods && (
                      <div style={HINT_STYLE}>
                        {t('school_requests_table.accept.requested_value', { value: requested.periods })}
                      </div>
                    )}
                  </td>

                  <td className='numeric' data-label={t('school_portal.requests.places')}>
                    <b>{state.removed ? NO_PLACE : granted.places}</b>
                    {!state.removed && granted.places !== requested.places && (
                      <div style={HINT_STYLE}>
                        {t('school_requests_table.accept.requested_value', { value: requested.places })}
                      </div>
                    )}
                  </td>

                  <td className='numeric actions'>
                    <div className='flex-row jc-end'>
                      {!expired && (
                        <TooltipIconButton
                          size='small'
                          type='primary'
                          ghost
                          icon={state.removed ? faArrowRotateLeft : (isAdjusting ? faCheck : faPen)}
                          title={t(state.removed
                            ? 'school_requests_table.accept.restore_line'
                            : (isAdjusting ? 'school_requests_table.accept.adjust_done' : 'school_requests_table.accept.adjust'))}
                          onClick={() => state.removed
                            ? patchGroup(group.key, { removed: false })
                            : setAdjusting(current => ({ ...current, [group.key]: !isAdjusting }))}
                        />
                      )}

                      {!excluded && (
                        <TooltipIconButton
                          className='ml-4'
                          size='small'
                          type='danger'
                          ghost
                          icon={faBan}
                          title={t('school_requests_table.accept.refuse_line')}
                          onClick={() => {
                            patchGroup(group.key, { removed: true })
                            setAdjusting(current => ({ ...current, [group.key]: false }))
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>,

                (group.sample.comment ?? '') !== '' && (
                  <tr key={`comment-${group.key}`} className='extras'>
                    <td colSpan={5}><i>{group.sample.comment}</i></td>
                  </tr>
                )
              ]
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>
                {t(readOnly && !isAnswered
                  ? 'school_requests_table.accept.total_asked'
                  : 'school_requests_table.accept.total')}
              </td>
              <td className='numeric'>
                {readOnly ? (isAnswered ? grantedPlaces : requestedPlaces) : keptPlaces}
                {(!readOnly || isAnswered) && (
                  <span style={HINT_STYLE}>
                    {t('school_requests_table.accept.total_requested', { requested: requestedPlaces })}
                  </span>
                )}
              </td>
              {!readOnly && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </Modal>
  )
}

export default AcceptSchoolRequestModal
